import React, { useCallback, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { BrandKitStore } from "@talentbait/shared-components";

import {
  Container,
  Sidebar,
  SidebarTitle,
  MainContent,
  SelectorSide,
  EditorSide,
  LibraryPortal
} from "./Layout.styled";

import PreviewArea from "../preview-area/PreviewArea";
import { ActionsMediaAssetsLab } from "../../../../../Actions/ActionsMediaAssetsLab/ActionsMediaAssetsLab";
import InputEditor from "../input-editor/InputEditor";
import { Text } from "../../../../../Components/Icons/Text";
import { Images } from "../../../../../Components/Icons/Images";
import AssetEditor from "../asset-editor/AssetEditor";
import { VoiceIcon } from "../../../../../Components/Icons";
import { OptionSelector } from "../options-selector/OptionSelector";
import AudioEditor from "../audio-editor/AudioEditor";
import {
  CampaignCreationStore,
  MediaAssetsLabStore,
  PreviewStore,
  TempDraftCreationStore,
  withRefluxStore
} from "../../../../../Stores";

import ControlPanel from "../preview-area/ControlPanel";
import { isVideo } from "../../utils";
import Actions from "../../../../../Actions";
import usePrevious from "../../../../../hooks/usePrevious";

const COMPANY_BRAND_KIT_FEATURE_FLAG = [
  "b1aGuxPuEESbSRh1c0GK",
  "0tGRWymrJbQU0WQwCRko",
  "ZmPQi4UDm6nxDNNDLTPf",
  "gEqObPuxpBeM9vJg8LDh",
  "-KqgHqZ1GUSverHcBxvO"
];

// Animation variants for sliding in from left to right
export const slideInVariants = {
  hidden: { x: "-9%", opacity: 0 }, // Start off-screen to the left
  visible: { x: 0, opacity: 1 }, // Slide in to the center
  exit: { x: "-9%", opacity: 0 } // Slide back out to the left
};

const headerOptions = [
  {
    id: "assets",
    text: "Assets",
    icon: <Images />
  },
  {
    id: "inputs",
    text: "Inputs",
    icon: <Text />
  },
  {
    id: "audio",
    text: "Audio",
    icon: <VoiceIcon />
  }
];

export const hasParamsAlready =
  /\/(w_\d+|h_\d+|c_[^/]+|g_[^/]+|ar_[^/]+)(?:,(w_\d+|h_\d+|c_[^/]+|g_[^/]+|ar_[^/]+))*\//;

export const removeCloudinaryParams = (url) => url.replace(hasParamsAlready, "/");

export const isImageCloudinary = (imageUrl = "") => imageUrl.includes("res.cloudinary.com/");

export const updateCloudinaryUrlParams = ({
  url,
  w,
  h,
  ar,
  maxDimension = 2000,
  replaceUrl = "upload"
}) => {
  if (!url) return url;

  function calculateDimensions(aspectRatioWidth, aspectRatioHeight, maxDim) {
    let width, height;
    const assetWidth = w > 3000 ? maxDim : w;
    const assetHeight = h > 3000 ? maxDim : h;

    if (aspectRatioWidth > aspectRatioHeight) {
      width = assetWidth;
      height = Math.round(assetWidth * (aspectRatioHeight / aspectRatioWidth));
    } else {
      height = assetHeight;
      width = Math.round(assetHeight * (aspectRatioWidth / aspectRatioHeight));
    }
    return { width, height };
  }

  const [ratioWidth, ratioHeight] = ar.split(":").map(parseFloat);
  const { width, height } = calculateDimensions(ratioWidth, ratioHeight, maxDimension);

  const cropParam = isVideo(url) ? "c_fill" : "c_auto";

  const newParams = `w_${width},h_${height},${cropParam},g_auto,ar_${ar}`;

  if (hasParamsAlready.test(url)) {
    return url.replace(hasParamsAlready, `/${newParams}/`);
  } else {
    return url.replace(`/${replaceUrl}/`, `/${replaceUrl}/${newParams}/`);
  }
};

export const updateCloudinaryUrlParamsWithoutCalculation = ({
  url,
  w,
  h,
  ar,
  replaceUrl = "upload"
}) => {
  if (!url) return url;

  const cropParam = isVideo(url) ? "c_fill" : "c_auto";
  const gParam = "g_auto";

  const newParams = `w_${w},h_${h},${cropParam},${gParam},ar_${ar}`;

  if (hasParamsAlready.test(url)) {
    return url.replace(hasParamsAlready, `/${newParams}/`);
  } else {
    const newUrl = url.replace(`/${replaceUrl}/`, `/${replaceUrl}/${newParams}/`);
    return newUrl;
  }
};

const Layout = ({
  campaignId,
  isReleased,
  // Store
  loadingCloudinaryImages,
  previewInputProps,
  isAutoGeneratedDataEmpty,
  organization,
  loadingDefaultImages,
  defaultCreatives,
  userProfile,
  companyName,
  channelsData,
  campaignData,
  previewInputPropsEdited,
  editedCampaignId,
  brandKitTemplateProps
}) => {
  const [configurationSelected, setConfigurationSelected] = useState("assets");
  const [openAssetLibrary, setOpenAssetLibrary] = useState(false);
  const previewBrandKitProps = usePrevious(brandKitTemplateProps);

  const getBrandKitTemplateProps = async () => {
    const getBrandKitCompletedRef = Actions.getBrandKitByOrgaId.completed.listen((res) => {
      if (res.payload) {
        const {
          primaryBackgroundColor,
          primaryTextColor,
          secondaryBackgroundColor,
          secondaryTextColor,
          fontFamily,
          logoPosition,
          logoSize,
          customFont
        } = res.payload;

        const inputProps = {
          primaryTextColor,
          primaryBackgroundColor,
          secondaryTextColor,
          secondaryBackgroundColor,
          fontFamily,
          logoPosition,
          logoSize,
          customFont
        };

        const brandKitLogos = Object.values(res.payload.logos);

        const companyLogos = brandKitLogos.length > 0 ? brandKitLogos : [organization.companyLogo];
        const filteredCompanyLogos = companyLogos.filter(Boolean);

        if (filteredCompanyLogos.length > 0) {
          ActionsMediaAssetsLab.setCompanyLogo(filteredCompanyLogos);
        }

        Object.entries(inputProps).forEach(([key, value]) => {
          Actions.saveProp({ id: key, value });
        });
      }

      getBrandKitCompletedRef();
    });

    const getBrandKitByCompanyIdCompletedRef = Actions.getBrandKitByCompanyId.completed.listen(
      (res) => {
        if (res.payload) {
          const {
            primaryBackgroundColor,
            primaryTextColor,
            secondaryBackgroundColor,
            secondaryTextColor,
            fontFamily,
            logoPosition,
            logoSize,
            customFont
          } = res.payload;

          const inputProps = {
            primaryTextColor,
            primaryBackgroundColor,
            secondaryTextColor,
            secondaryBackgroundColor,
            fontFamily,
            logoPosition,
            logoSize,
            customFont
          };

          const brandKitLogos = Object.values(res.payload.logos);

          const companyLogos =
            brandKitLogos.length > 0 ? brandKitLogos : [organization.companyLogo];
          const filteredCompanyLogos = companyLogos.filter(Boolean);

          if (filteredCompanyLogos.length > 0) {
            ActionsMediaAssetsLab.setCompanyLogo(filteredCompanyLogos);
          }

          Object.entries(inputProps).forEach(([key, value]) => {
            Actions.saveProp({ id: key, value });
          });
        }

        getBrandKitByCompanyIdCompletedRef();
      }
    );
  };

  useEffect(() => {
    let jobTitle = campaignData?.jobTitle || previewInputProps?.text;

    if (isAutoGeneratedDataEmpty) {
      regenerateText({ text: jobTitle });
    }

    ActionsMediaAssetsLab.changePreviewInputProps({
      key: "text",
      value: jobTitle
    });
  }, []);

  useEffect(() => {
    getBrandKitTemplateProps();
    const { companyId, organizationId } = campaignData;
    if (companyId) {
      Actions.getBrandKitByCompanyId(companyId);
    } else {
      Actions.getBrandKitByOrgaId(organizationId);
    }
  }, [campaignData?.companyId, campaignData?.organizationId]);

  useEffect(() => {
    const isSameCampaign = campaignId === editedCampaignId;
    const keepSameBrandKitProps = previewInputPropsEdited && isSameCampaign;

    if (keepSameBrandKitProps) return;

    const {
      primaryTextColor,
      primaryBackgroundColor,
      secondaryBackgroundColor,
      secondaryTextColor,
      fontFamily,
      logoPosition,
      logoSize,
      customFont
    } = brandKitTemplateProps;

    const mapBrandKitTemplateProps = {
      primaryTextColor,
      primaryBackgroundColor,
      secondaryTextColor,
      secondaryBackgroundColor,
      fontFamily,
      logoPosition,
      logoSize,
      customFont
    };

    Object.entries(mapBrandKitTemplateProps).forEach(([key, value]) => {
      ActionsMediaAssetsLab.changePreviewInputProps({ key, value });
    });
  }, [brandKitTemplateProps, previewInputPropsEdited, previewBrandKitProps, editedCampaignId]);

  useEffect(() => {
    ActionsMediaAssetsLab.setEditedCampaignId(campaignId);

    if (campaignId && !loadingCloudinaryImages && !loadingDefaultImages) {
      ActionsMediaAssetsLab.getCloudinaryImages(campaignId, defaultCreatives);
    }

    if (campaignId && !loadingDefaultImages && !loadingCloudinaryImages) {
      ActionsMediaAssetsLab.getAssetsByCampaignId(campaignId, defaultCreatives);
    }
  }, [campaignId]);

  const regenerateText = useCallback(
    ({ text, language, formality }) => {
      ActionsMediaAssetsLab.changePreviewInputProps({
        key: "isRegeneratingLabText",
        value: true
      });

      ActionsMediaAssetsLab.generateMediaAssetsTextStream({
        channel: "spotify",
        jobTitle: text || previewInputProps?.text,
        jobPostingUrl: channelsData.spotify?.[0]?.url,
        orgaName: companyName || campaignData?.companyName,
        orgaId: organization?.id || campaignData?.orgaId,
        userId: userProfile?.userId,
        userName: userProfile?.userName,
        language: language || "German",
        formality: formality || "Sie"
      });
    },
    [organization, previewInputProps?.text, companyName, channelsData?.spotify]
  );

  const renderEditor = (type) => {
    switch (type) {
      case "inputs":
        return (
          <motion.div
            key="input-editor"
            variants={slideInVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.5 }}
            style={{ position: "relative", top: 0, margin: 0, padding: 0 }}
          >
            <SidebarTitle>Input Editor</SidebarTitle>
            <InputEditor brandKitTemplateProps={brandKitTemplateProps} />
          </motion.div>
        );
      case "assets":
        return (
          <motion.div
            key="asset-editor"
            variants={slideInVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.5 }}
            style={{ position: "relative", top: 0, margin: 0, padding: 0 }}
          >
            <SidebarTitle>Asset Editor</SidebarTitle>
            <AssetEditor
              openAssetLibrary={openAssetLibrary}
              setOpenAssetLibrary={setOpenAssetLibrary}
            />
          </motion.div>
        );
      case "audio":
        return (
          <motion.div
            key="audio-editor"
            variants={slideInVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.5 }}
            style={{ position: "relative", top: 0, margin: 0, padding: 0 }}
          >
            <SidebarTitle>Audio Editor</SidebarTitle>
            <AudioEditor
              campaignId={campaignId}
              regenerateText={regenerateText}
              isRegeneratingLabText={previewInputProps.isRegeneratingLabText}
            />
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Sidebar>
        <SelectorSide>
          {headerOptions.map(({ id, text, icon }) => (
            <OptionSelector
              key={id}
              onClick={() => setConfigurationSelected(id)}
              isSelected={configurationSelected === id}
              icon={icon}
              text={text}
            />
          ))}
        </SelectorSide>
        <EditorSide key={configurationSelected}>
          <AnimatePresence>{renderEditor(configurationSelected)}</AnimatePresence>
        </EditorSide>
      </Sidebar>

      <MainContent>
        <ControlPanel
          isReleased={isReleased}
          campaignId={campaignId}
          previewInputProps={previewInputProps}
        />
        <PreviewArea />
        <LibraryPortal id="asset-library-portal" isElevated={openAssetLibrary} />
      </MainContent>
    </Container>
  );
};

export default withRouter(
  withRefluxStore(
    Layout,
    [
      CampaignCreationStore,
      MediaAssetsLabStore,
      TempDraftCreationStore,
      PreviewStore,
      BrandKitStore
    ],
    [
      "loadingCloudinaryImages",
      "previewInputProps",
      "isAutoGeneratedDataEmpty",
      "organization",
      "loadingDefaultImages",
      "defaultCreatives",
      "userProfile",
      "companyName",
      "channelsData",
      "campaignData",
      "previewInputPropsEdited",
      "editedCampaignId",
      "brandKitTemplateProps"
    ]
  )
);
